'use client';

import { useEffect } from 'react';
import { Search, Database, Network, Building2, Home, CheckCircle2 } from 'lucide-react';
import { SitePage, PageHero, Section, Card, IconBadge, Btn } from '@/components/site/page-kit';

const problems = [
  {
    title: "Material Discovery Challenge",
    problem: "Architects and designers struggle to discover new laminates, panels, and interior materials across multiple brands.",
    solution: "HINCH provides a centralized platform with thousands of curated material designs and finishes, searchable by color, texture, application, and brand.",
    icon: Search
  },
  {
    title: "Fragmented Supplier Network",
    problem: "Interior materials are sold through scattered dealers with inconsistent catalogs.",
    solution: "HINCH aggregates materials, brands, and suppliers in one searchable platform with verified supplier connectivity.",
    icon: Network
  },
  {
    title: "Lack of Structured Information",
    problem: "Interior material catalogs lack standardized specifications and comparison data.",
    solution: "HINCH provides structured product information including finish type, color family, applications, dimensions, and technical specifications.",
    icon: Database
  }
];

const materials = [
  'Decorative Laminates', 'Acrylic Laminates', 'Plywood & Boards', 'MDF & HDF Panels',
  'Decorative Wall Panels', 'Louvers & Fluted Panels', 'Interior Hardware', 'Architectural Surfaces'
];

export default function WhatIsHinchPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <SitePage>
      <PageHero eyebrow="About HINCH" title="What is HINCH?">
        <p className="mt-6 max-w-[720px] border-l-2 border-hv-orange pl-5 text-lg leading-relaxed text-hv-ink md:text-[22px]">
          HINCH is an <strong className="font-semibold">omnichannel platform for discovering and sourcing home interior materials</strong>, 
          enabling architects, interior designers, furniture manufacturers, and homeowners to explore, compare, 
          and procure materials such as <strong className="font-semibold">laminates, plywood, decorative panels, interior hardware, louvers, 
          and architectural surfaces</strong> across India.
        </p>
      </PageHero>

      <Section tone="white" title="Platform Category">
        <div className="grid gap-3 md:grid-cols-3 md:gap-4">
          {[
            { label: 'Primary Category', value: 'Interior Materials Discovery Platform' },
            { label: 'Industry', value: 'Building Materials & Interior Design' },
            { label: 'Market', value: 'India (Primary), Middle East (Expansion)' },
          ].map((item) => (
            <div key={item.label} className="rounded-2xl border border-hv-line bg-hv-paper p-6">
              <h3 className="mb-2"><span className="font-hv-mono text-[12px] font-medium uppercase tracking-[0.04em] text-hv-orange">{item.label}</span></h3>
              <p className="text-lg text-hv-ink">{item.value}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Problems HINCH Solves">
        <div className="space-y-3 md:space-y-4">
          {problems.map((item, index) => (
            <Card key={index}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <IconBadge icon={item.icon} />
                <div className="flex-1">
                  <h3 className="mb-4 text-xl">{item.title}</h3>
                  <div className="grid gap-4 md:grid-cols-2 md:gap-6">
                    <div>
                      <p className="mb-1.5 font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-soft">Problem:</p>
                      <p className="text-[15px] leading-relaxed text-hv-soft">{item.problem}</p>
                    </div>
                    <div className="rounded-xl bg-hv-card p-4">
                      <p className="mb-1.5 font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-orange">Solution:</p>
                      <p className="text-[15px] leading-relaxed text-hv-ink">{item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="card" title="Who Uses HINCH?">
        <div className="grid gap-3 md:grid-cols-2 md:gap-4">
          {[
            { icon: Building2, title: 'B2B Professionals', items: ['Interior Designers sourcing materials for client projects',
              'Architects specifying materials for residential/commercial builds',
              'Furniture Manufacturers procuring laminates and hardware',
              'Contractors finding materials for construction projects'] },
            { icon: Home, title: 'B2C Customers', items: ['Homeowners building new homes',
              'Individuals renovating existing spaces',
              'DIY enthusiasts exploring interior materials',
              'Property developers sourcing bulk materials'] },
          ].map((group) => (
            <Card key={group.title}>
              <div className="mb-6 flex items-center gap-3">
                <IconBadge icon={group.icon} tone="yellow" />
                <h3 className="text-xl">{group.title}</h3>
              </div>
              <ul className="space-y-3">
                {group.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-hv-orange" strokeWidth={1.8} />
                    <span className="text-hv-soft">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Materials Available on HINCH">
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {materials.map((material, index) => (
            <li key={index} className="flex min-h-[88px] items-center rounded-xl border border-hv-line bg-white p-4 text-[15px] font-medium text-hv-ink md:p-5">
              {material}
            </li>
          ))}
        </ul>
      </Section>

      <Section tone="dark">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          <h2 className="text-[28px] leading-[1.08] tracking-[-0.02em] md:text-[40px]">Industry Authority</h2>
          <div className="space-y-5 text-base leading-relaxed text-white/75 md:text-lg">
            <p>
              HINCH is building one of the <strong className="font-semibold text-white">largest structured digital catalogs of interior materials in India</strong>, 
              enabling design professionals to explore thousands of finishes, colors, and material applications across leading brands.
            </p>
            <p>
              The platform aims to become the <strong className="font-semibold text-white">default discovery engine for interior materials</strong> used in 
              residential and commercial interior design projects across India.
            </p>
          </div>
        </div>
      </Section>

      <Section>
        <div className="flex flex-col items-start gap-6 rounded-[20px] bg-hv-yellow p-6 md:flex-row md:items-center md:justify-between md:p-11">
          <div>
            <h2 className="mb-2 text-[26px] leading-[1.1] tracking-[-0.02em] md:text-[36px]">Ready to Explore?</h2>
            <p className="text-hv-ink/80">Discover thousands of interior materials on HINCH</p>
          </div>
          <Btn href="/" kind="dark" arrow>Browse Materials</Btn>
        </div>
      </Section>
    </SitePage>
  );
}
